import { useCallback, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Ship, Cog, Package, MapPin, MoreHorizontal } from 'lucide-react';
import { useHaptics } from '../../hooks/useHaptics';
import { useDeviceMode } from '../../hooks/useDeviceMode';

interface PwaBottomNavProps {
  onOpenMore: () => void;
}

const TABS = [
  { path: '/boats', label: 'Barche', icon: Ship },
  { path: '/motors', label: 'Motori', icon: Cog },
  { path: '/accessories', label: 'Accessori', icon: Package },
  { path: null, label: 'Mappa', icon: MapPin, isMap: true },
] as const;

export default function PwaBottomNav({ onOpenMore }: PwaBottomNavProps) {
  const { impactMedium } = useHaptics();
  const { isPWA, isMobileWeb } = useDeviceMode();
  const location = useLocation();
  const [pressed, setPressed] = useState<string | null>(null);

  const mapPath = (isPWA || isMobileWeb) ? '/map-explorer-pwa' : '/map-explorer-desktop';

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const handlePress = useCallback((key: string) => {
    impactMedium();
    setPressed(key);
    setTimeout(() => setPressed(null), 140);
  }, [impactMedium]);

  return (
    <nav
      className="au-tabbar"
      aria-label="Navigazione principale"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {TABS.map((tab) =>
        tab.path ? (
          <NavLink
            key={tab.label}
            to={tab.path}
            onClick={() => handlePress(tab.path)}
            className={`au-tab ${isActive(tab.path) ? 'is-active' : ''} ${pressed === tab.path ? 'scale-90' : ''}`}
            style={isActive(tab.path) ? { color: 'var(--au-cyan)' } : undefined}
            aria-label={tab.label}
            aria-current={isActive(tab.path) ? 'page' : undefined}
          >
            {isActive(tab.path) && <span className="au-tab-glow" aria-hidden="true" />}
            <tab.icon className="w-[22px] h-[22px]" aria-hidden="true" />
            <span className="au-tab-label">{tab.label}</span>
          </NavLink>
        ) : (
          <NavLink
            key={tab.label}
            to={mapPath}
            onClick={() => handlePress(mapPath)}
            className={`au-tab ${isActive(mapPath) ? 'is-active' : ''} ${pressed === mapPath ? 'scale-90' : ''}`}
            style={isActive(mapPath) ? { color: 'var(--au-cyan)' } : undefined}
            aria-label={tab.label}
            aria-current={isActive(mapPath) ? 'page' : undefined}
          >
            {isActive(mapPath) && <span className="au-tab-glow" aria-hidden="true" />}
            <tab.icon className="w-[22px] h-[22px]" aria-hidden="true" />
            <span className="au-tab-label">{tab.label}</span>
          </NavLink>
        )
      )}

      <button
        onClick={() => {
          handlePress('more');
          onOpenMore();
        }}
        className={`au-tab ${pressed === 'more' ? 'scale-90' : ''}`}
        aria-label="Altro menu"
        type="button"
      >
        <MoreHorizontal className="w-[22px] h-[22px]" aria-hidden="true" />
        <span className="au-tab-label">Altro</span>
      </button>
    </nav>
  );
}
